import {
  RECIPIENT,
  type ChatQuestion,
} from '@/karehero-app/tell-us-once/intake/content';
import {
  INTAKE_CHAT_V2_0_DEV,
  INTAKE_CHAT_V2_0_DEV_BRANCHES,
} from '@/karehero-app/tell-us-once/intake/content.v2-0-dev';

/**
 * Onboarding 2.1: 2.0 as it is on dev, with a few turns tweaked. Everything
 * not named here is 2.0's own.
 */

/** The opening's two answers. */
export const READY = "I'm ready";
export const TELL_ME_MORE = 'Tell me more';
/** The consent turn's answers. */
export const HAS_CONSENT = 'Yes, I do';
export const NO_CONSENT = 'No';

/** The idea's way out: to the form, not to a call. */
export const FORM_INSTEAD = "I'd rather fill in a form";

/**
 * The opening, without the AI consent: we don't need to ask for it here, so
 * the first message only says what we do. Both answers are chips above the
 * field, so the composer is there from the start rather than popping up once
 * they answer. Ready carries on; Tell me more is answered in the chat.
 */
const WELCOME: ChatQuestion = {
  id: 'consent',
  // How long it takes, up front. Check the 3 minutes against how long V4
  // takes in PostHog.
  ask: 'Hi, our care experts help people find, fund and understand care, from working out what you could claim to finding the right carers. It takes about 3 minutes.',
  mode: 'say',
  quickReplies: [{ label: READY }, { label: TELL_ME_MORE, secondary: true }],
  next: { [TELL_ME_MORE]: 'welcome-more' },
  answer: READY,
};

/**
 * Tell me more, said rather than opened in a sheet: what we do in a few
 * messages, the AI line among them, then the same way on.
 */
const WELCOME_MORE: ChatQuestion = {
  id: 'welcome-more',
  says: [
    'You can tell us about your situation in your own words. Type it, say it, or send a photo of a letter.',
    'We use AI to help our care experts read and organise what you share, so nothing gets lost and you never have to say it twice. A real person always looks over it.',
  ],
  ask: 'Ready to start?',
  mode: 'say',
  quickReplies: [{ label: READY }],
  next: { '*': 'help' },
  answer: READY,
};

/**
 * Consent to share. The old form onboarding asked it as a checkbox on the care
 * recipient's name ("I have consent, or Power of Attorney, to share sensitive
 * information about the person to support", care-recipient-consent-v1 on
 * main), and V4 dropped it.
 *
 * Asked once the summary is confirmed, not first: by then what they said has
 * been read, so we know whether they are looking after someone else. If it is
 * for themselves, it is not asked at all.
 *
 * No is reassured and carries on: see afterNoConsent.
 */
const SHARE_CONSENT: ChatQuestion = {
  id: 'share-consent',
  ask: `Thanks. Before we go on, do you currently have ${RECIPIENT}'s consent, or Power of Attorney, to share sensitive information about them?`,
  mode: 'say',
  quickReplies: [
    { label: HAS_CONSENT },
    { label: NO_CONSENT, secondary: true },
  ],
  next: { [NO_CONSENT]: 'location-no-consent' },
  answer: HAS_CONSENT,
};

/**
 * No, and it carries on: reassured first, because we help with Power of
 * Attorney (the Poa view on main), then where they live as normal.
 */
const afterNoConsent = (location: ChatQuestion): ChatQuestion => ({
  ...location,
  id: 'location-no-consent',
  says: [
    "Don't worry, we can help you with that later on.",
    ...(location.says ?? []),
  ],
  next: { '*': 'age' },
});

/**
 * How far along they are, said rather than drawn: no stepper, because a bar
 * says the length is fixed and dynamic flows will change it. Keni marks the
 * turning points instead, by question id.
 */
const SIGNPOSTS: Record<string, (q: ChatQuestion) => ChatQuestion> = {
  // After the summary, the telling is done and only small questions are left.
  location: (q) => ({
    ...q,
    says: ['Thank you. There are a few more things we need to know.'],
  }),
};

const signposted = (q: ChatQuestion) => SIGNPOSTS[q.id]?.(q) ?? q;

/**
 * 2.1 from a 2.0 dev conversation: the opening swapped, and consent asked
 * after the summary unless it is for themselves.
 */
export const asV21 = (
  base: ChatQuestion[],
  askConsent: boolean,
): ChatQuestion[] => [
  ...base
    .map(signposted)
    .flatMap((q) =>
      q.id === 'consent'
        ? [WELCOME]
        : q.id === 'summary' && askConsent
          ? [q, SHARE_CONSENT]
          : [q],
    ),
  // Off the main path, after its end.
  WELCOME_MORE,
  ...(askConsent
    ? base
        .filter((q) => q.id === 'location')
        .map((q) => afterNoConsent(signposted(q)))
    : []),
];

export const INTAKE_CHAT_V2_1: ChatQuestion[] = asV21(
  INTAKE_CHAT_V2_0_DEV,
  true,
);

/** Looking after themselves, so consent is never asked. */
export const INTAKE_CHAT_V2_1_SELF: ChatQuestion[] = asV21(
  INTAKE_CHAT_V2_0_DEV_BRANCHES.self,
  false,
);

/** The same, with Tell me more picked, to open on what it says. */
export const INTAKE_CHAT_V2_1_MORE: ChatQuestion[] = INTAKE_CHAT_V2_1.map(
  (q) => (q.id === 'consent' ? { ...q, answer: TELL_ME_MORE } : q),
);

/**
 * An idea, and the exit for A/B testing the form: Tell me more with a quieter
 * third chip that leaves the chat for the form onboarding. Only here, once
 * they have read what we do and are still unsure, and to the form rather than
 * a call, so the unsure don't all land on the care expert team.
 */
export const INTAKE_CHAT_V2_1_EXIT: ChatQuestion[] = INTAKE_CHAT_V2_1_MORE.map(
  (q) =>
    q.id === 'welcome-more'
      ? {
          ...q,
          quickReplies: [
            ...(q.quickReplies ?? []),
            { label: FORM_INSTEAD, secondary: true },
          ],
          leaves: FORM_INSTEAD,
        }
      : q,
);

/** The same, with No picked on consent, to open on what it says. */
export const INTAKE_CHAT_V2_1_NO_CONSENT: ChatQuestion[] = INTAKE_CHAT_V2_1.map(
  (q) => (q.id === 'share-consent' ? { ...q, answer: NO_CONSENT } : q),
);

/**
 * If what they said didn't cover what they need help with, it's asked as the
 * final question rather than straight after the summary, the way 2.0 does: the
 * summary goes on as normal, and it comes once the small questions are done.
 */
const NEED_HELP_LAST: ChatQuestion = {
  id: 'need-help-last',
  ask: 'One last thing. So our care experts know where to start, what would you most like help with?',
  mode: 'say',
  reading: {
    lines: ["Going through what you've shared", 'Adding it to your summary'],
    ms: 3600,
  },
  answer: 'Money Mum might be owed, and care at home.',
  next: { '*': 'start' },
};

export const INTAKE_CHAT_V2_1_NO_NEED: ChatQuestion[] = [
  ...asV21(
    INTAKE_CHAT_V2_0_DEV_BRANCHES['need-help'].map((q) =>
      q.id === 'summary'
        ? { ...q, next: { 'Not quite': 'not-quite' } }
        : q.id === 'phone'
          ? { ...q, next: { '*': 'need-help-last' } }
          : q,
    ),
    true,
  ),
  NEED_HELP_LAST,
];
